<template>
  <div>
    <div
      class="flex text-lg font-medium items-center cursor-pointer w-full"
      @click="open = !open"
    >
      <button
        v-if="hasValidChildren"
        type="button"
        class="btn-icon btn-icon-default"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5 transition-transform duration-200"
          :class="[{'rotate-90': open}]">
  <path fill-rule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clip-rule="evenodd" />
</svg>
      </button>

      <p class="w-80 text-wrap ml-1">
        {{ trade.billNumber }})

        {{ trade.name }}
      </p>

      <input
        v-if="hasValidChildren"
        type="checkbox"
        class="form-checkbox ml-auto"
        :checked="tradeSelected"
        :disabled="allSelectableItemsAssignedToPackage"
        @click.stop="onTradeCheckboxClick"
      >

      <div
        v-if="hasValidChildren"
        class="w-4 h-4 rounded-full ml-2"
        :class="{
          'bg-red-300': tradeItemsAssignedToPackage === 0, 'bg-orange-400': tradeItemsAssignedToPackage > 0 && tradeItemsAssignedToPackage < selectableItems, 'bg-green-400': tradeItemsAssignedToPackage === selectableItems
        }"
        :title="indicatorTitle"
      />
    </div>

    <div
      v-if="open"
      class="ml-4 flex flex-col space-y-0.5 mt-1"
    >
      <TradeItemSelector
        v-for="tradeItem in trade.trade_items"
        :key="tradeItem.id"
        :trade-item="tradeItem"
        :package-trade-item="tradeItem.package_trade_items.find((packageTradeItem) => packageTradeItem.package_id === $props.package.id)"
        :is-selected="selectedTradeItems.findIndex((selectedTradeItem) => selectedTradeItem.id === tradeItem.id) >= 0"
        :selected-trade-items="selectedTradeItems"
        :package="$props.package"
        @select-trade-item="$emit('selectTradeItem', $event?.tradeItem ? $event.tradeItem : tradeItem)"
        @deselect-trade-item="$emit('deselectTradeItem', $event?.tradeItem ? $event.tradeItem : tradeItem )"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { t } from '@/i18n';
import Package from '@/models/Package';
import Trade from '@/models/Trade';
import TradeItem from '@/models/TradeItem';
import { useTradeItemsStore } from '@/store/tradeItems';
import { computed, ref } from 'vue';
import TradeItemSelector from './TradeItemSelector.vue';

const props = defineProps<{
  trade: Trade;
  package: Package;
  selectedTradeItems: TradeItem[];
}>();

const emits = defineEmits<{
  (e: 'selectTradeItem', tradeItem: TradeItem);
  (e: 'deselectTradeItem', tradeItem: TradeItem);
}>();

const open = ref(false);
const tradeItemsStore = useTradeItemsStore();

function collectSelectableLeaves(tradeItem: TradeItem): TradeItem[] {
  const tradeItemsStore = useTradeItemsStore();

  if(tradeItem.has_children) {
    return tradeItemsStore.models
      .where('parent_id', tradeItem.id)
      .with('package_trade_items')
      .where((item) => item.budget > 0 || item.has_children)
      .orderBy('sort_order')
      .get()
      .flatMap(collectSelectableLeaves);
  }

  return tradeItem.budget > 0 ? [tradeItem] : [];
}

function getSelectableLeafItemsForTrade(tradeId: Id): TradeItem[] {
  const tradeItemsStore = useTradeItemsStore();

  return tradeItemsStore.models
    .where('trade_id', Number(tradeId))
    .whereNull('parent_id')
    .with('package_trade_items')
    .where((item) => item.budget > 0 || item.has_children)
    .orderBy('sort_order')
    .get()
    .flatMap(collectSelectableLeaves);
}

function isTradeItemAssignedToPackage(tradeItem: TradeItem, packageId: Id): boolean {
  return tradeItem.package_trade_items.some((packageTradeItem) => packageTradeItem.package_id === packageId);
}

const tradeItemsAssignedToPackage = computed(() => {
  return selectableLeafItems.value.filter((tradeItem) => {
    return tradeItem.package_trade_items.length > 0;
  }).length;
});

const selectableLeafItems = computed(() => {
  return getSelectableLeafItemsForTrade(props.trade.id);
});

const selectableItems = computed(() => {
  return selectableLeafItems.value.length;
});

const indicatorTitle = computed(() => {
  if(tradeItemsAssignedToPackage.value > 0 && tradeItemsAssignedToPackage.value < selectableItems.value) {
    return t('Subcontract.PackagesDashboard.TradeRow.message.someItemsAssigned');
  } else if(tradeItemsAssignedToPackage.value === selectableItems.value && selectableItems.value > 0) {
    return t('Subcontract.PackagesDashboard.TradeRow.message.allItemsAssigned');
  }

  return t('Subcontract.PackagesDashboard.TradeRow.message.noItemsAssigned');
});

const tradeItemsAssignedToSelectedPackage = computed(() => {
  return selectableLeafItems.value.filter((tradeItem) => {
    return isTradeItemAssignedToPackage(tradeItem, props.package.id);
  }).length;
});

const tradeItemsInSelectedPackage = computed(() => {
  return tradeItemsAssignedToSelectedPackage.value +
    props.selectedTradeItems.filter((tradeItem) => tradeItem.trade_id === props.trade.id).length;
});

const allSelectableItemsAssignedToPackage = computed(() => {
  return selectableItems.value > 0 && tradeItemsAssignedToSelectedPackage.value === selectableItems.value;
});

const tradeSelected = computed(() => {
  return selectableItems.value > 0 && tradeItemsInSelectedPackage.value === selectableItems.value;
});

const selectAllItems = (tradeItem: TradeItem) => {
  if(tradeItem.has_children) {
    const childItems = tradeItemsStore.models.with('package_trade_items', (query) => {
      query.with('package');
    }).where('parent_id', tradeItem.id)
      .where((item) => item.budget > 0 || item.has_children)
      .get();

    childItems.forEach((childItem) => {
      selectAllItems(childItem);
    });
  } else if(
    !isTradeItemAssignedToPackage(tradeItem, props.package.id) && tradeItem.budget > 0
  ) {
    emits('selectTradeItem', tradeItem);
  }
};

const deselectAllItems = () => {
  props.selectedTradeItems.forEach((tradeItem) => {
    if(tradeItem.trade_id === props.trade.id) {
      emits('deselectTradeItem', tradeItem);
    }
  });
};

const onTradeCheckboxClick = () => {
  if(tradeSelected.value) {
    deselectAllItems();
  } else {
    props.trade.trade_items.forEach((tradeItem) => {
      selectAllItems(tradeItem);
    });
  }
};

const hasValidChildren = computed(() => {
  return tradeItemsStore.models.where('trade_id', props.trade.id).where('budget', (budget) => budget > 0).get().length >
    0;
});
</script>

<style scoped></style>
